import React, { useEffect, useState } from 'react';
import { FiEye, FiCheck, FiX } from 'react-icons/fi';
import { listPaymentsData, updatePaymentStatus, updateOrderServiceStatus, updateOrderStatus } from '../services/dbService';
import toast from 'react-hot-toast';
import './Payments.css';

const Payments = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [statusFilter, setStatusFilter] = useState('pending');
    const [actionLoading, setActionLoading] = useState(false);
    const [imgPreview, setImgPreview] = useState(null);

    useEffect(() => {
        fetchPayments();
    }, []);

    const fetchPayments = async () => {
        try {
            setLoading(true);
            const data = await listPaymentsData();
            setPayments(data || []);
        } catch (err) {
            console.error(err);
            toast.error('Failed to fetch payments');
        } finally {
            setLoading(false);
        }
    };

    const handleServicePaymentClick = async (paymentId, itemIndex, currentStatus) => {
        const newStatus = currentStatus === 'paid' ? 'pending' : 'paid';
        try {
            const payment = payments.find(p => p.id === paymentId);
            const item = payment.items[itemIndex];
            if (!item?.id) return;

            await updateOrderServiceStatus(item.id, newStatus);
            toast.success(`Service marked as ${newStatus}`);

            setPayments(prev => prev.map(p => {
                if (p.id !== paymentId) return p;
                const newItems = [...p.items];
                newItems[itemIndex] = { ...item, status: newStatus };
                return { ...p, items: newItems };
            }));
        } catch (err) {
            console.error(err);
            toast.error('Failed to update service');
        }
    };

    const handleVerifyPayment = async (paymentId, isApproved) => {
        const action = isApproved ? 'approve' : 'reject';
        if (!window.confirm(`Are you sure you want to ${action} this payment?`)) return;
        try {
            setActionLoading(true);
            const newStatus = isApproved ? 'verified' : 'rejected';
            await updatePaymentStatus(paymentId, newStatus);

            // Also update the linked order status
            const pmt = payments.find(p => p.id === paymentId);
            if (pmt?.orderId) {
                await updateOrderStatus(pmt.userId || pmt.payer_id, pmt.orderId, {
                    status: isApproved ? 'paid' : 'rejected',
                }).catch(err => console.warn('Order status update failed:', err.message));
            }

            toast.success(`Payment ${action}d successfully`);
            setShowDetailModal(false);
            fetchPayments();
        } catch (err) {
            console.error(err);
            toast.error(err.message || `Failed to ${action} payment`);
        } finally {
            setActionLoading(false);
        }
    };

    const filteredPayments = payments.filter(p => {
        if (statusFilter === 'pending') return p.status === 'pending';
        if (statusFilter === 'verified') return p.status === 'verified';
        if (statusFilter === 'rejected') return p.status === 'rejected';
        return true;
    });

    const getStatusBadge = (status) => {
        const map = {
            pending:  { color: '#ecc94b', text: 'Pending' },
            verified: { color: '#48bb78', text: 'Verified' },
            rejected: { color: '#f56565', text: 'Rejected' },
        };
        return map[status] || { color: '#a0aec0', text: status || 'Unknown' };
    };

    const isImageUrl = (url) => {
        if (!url) return false;
        return /\.(jpg|jpeg|png|gif|webp|bmp|svg)/i.test(url) ||
               url.includes('cloudinary') ||
               url.includes('supabase.storage');
    };

    if (loading) return (
        <div className="payments-container">
            <div style={{ textAlign: 'center', padding: '60px', color: '#718096' }}>
                <p>Loading payments...</p>
            </div>
        </div>
    );

    return (
        <div className="payments-container animate-fade-in">
            <h1>Payment Verification</h1>

            <div className="filter-bar">
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="verified">Verified</option>
                    <option value="rejected">Rejected</option>
                </select>
            </div>

            <div className="payments-table-wrapper">
                <table className="payments-table">
                    <thead>
                        <tr>
                            <th>Payment ID</th>
                            <th>Customer</th>
                            <th>Receipt Image</th>
                            <th>Services Ordered</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPayments.length > 0 ? (
                            filteredPayments.map(payment => {
                                const badge = getStatusBadge(payment.status);
                                return (
                                    <tr key={payment.id}>
                                        {/* Payment ID */}
                                        <td className="order-id">{payment.id?.slice(-8)}</td>

                                        {/* Customer: name + phone */}
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <strong>{payment.payerName}</strong>
                                                {payment.payerPhone && payment.payerPhone !== 'N/A' && (
                                                    <span style={{ fontSize: '0.82em', color: '#718096' }}>
                                                        {payment.payerPhone}
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        {/* Receipt Image */}
                                        <td>
                                            {payment.proofScreenshot ? (
                                                isImageUrl(payment.proofScreenshot) ? (
                                                    <img
                                                        src={payment.proofScreenshot}
                                                        alt="Receipt"
                                                        style={{
                                                            width: '80px',
                                                            height: '64px',
                                                            objectFit: 'cover',
                                                            borderRadius: '6px',
                                                            border: '1px solid #e2e8f0',
                                                            cursor: 'zoom-in',
                                                            display: 'block'
                                                        }}
                                                        onClick={() => setImgPreview(payment.proofScreenshot)}
                                                        onError={e => {
                                                            e.target.style.display = 'none';
                                                            const link = e.target.nextSibling;
                                                            if (link) link.style.display = 'inline';
                                                        }}
                                                    />
                                                ) : (
                                                    <a href={payment.proofScreenshot} target="_blank" rel="noreferrer" style={{ color: '#4299e1', textDecoration: 'underline', fontSize: '0.88em' }}>
                                                        View Doc
                                                    </a>
                                                )
                                            ) : (
                                                <span style={{ color: '#a0aec0', fontSize: '0.85em' }}>No receipt</span>
                                            )}
                                            {/* hidden fallback link if img errors */}
                                            {payment.proofScreenshot && isImageUrl(payment.proofScreenshot) && (
                                                <a href={payment.proofScreenshot} target="_blank" rel="noreferrer" style={{ display: 'none', color: '#4299e1', textDecoration: 'underline', fontSize: '0.85em' }}>View</a>
                                            )}
                                        </td>

                                        {/* Services Ordered */}
                                        <td>
                                            {payment.items && payment.items.length > 0 ? (
                                                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                                                    {payment.items.map((item, idx) => (
                                                        <li key={idx} style={{ marginBottom: '6px', paddingBottom: '4px', borderBottom: '1px solid #f0f0f0' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                                                <div>
                                                                    <strong style={{ fontSize: '0.88em' }}>
                                                                        {item.service_title || item.serviceDetails?.name || 'Unknown'}
                                                                    </strong>
                                                                    {item.serviceDetails?.category && (
                                                                        <><br /><span style={{ fontSize: '0.76em', color: '#888' }}>{item.serviceDetails.category}</span></>
                                                                    )}
                                                                </div>
                                                                <button
                                                                    style={{
                                                                        padding: '2px 8px', fontSize: '0.78em', cursor: 'pointer',
                                                                        backgroundColor: item.status === 'paid' ? '#48bb78' : '#ecc94b',
                                                                        color: '#fff', border: 'none', borderRadius: '4px', whiteSpace: 'nowrap'
                                                                    }}
                                                                    onClick={() => handleServicePaymentClick(payment.id, idx, item.status)}
                                                                >
                                                                    {item.status === 'paid' ? 'Paid ✓' : 'Mark Paid'}
                                                                </button>
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <span style={{ color: '#a0aec0', fontSize: '0.85em' }}>No services</span>
                                            )}
                                        </td>

                                        {/* Status badge */}
                                        <td>
                                            <span className="status-badge" style={{ backgroundColor: badge.color }}>
                                                {badge.text}
                                            </span>
                                        </td>

                                        {/* Actions */}
                                        <td className="action-buttons">
                                            <button className="btn btn-sm btn-info" onClick={() => { setSelectedPayment(payment); setShowDetailModal(true); }}>
                                                <FiEye /> View
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="6" className="text-center">No payments found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Full-screen image lightbox */}
            {imgPreview && (
                <div
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 9999, cursor: 'zoom-out'
                    }}
                    onClick={() => setImgPreview(null)}
                >
                    <img
                        src={imgPreview}
                        alt="Receipt full view"
                        style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: '10px', boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}
                        onClick={e => e.stopPropagation()}
                    />
                    <button
                        onClick={() => setImgPreview(null)}
                        style={{
                            position: 'fixed', top: 20, right: 28, background: 'rgba(255,255,255,0.15)',
                            border: 'none', color: '#fff', fontSize: '2rem', cursor: 'pointer',
                            borderRadius: '50%', width: '44px', height: '44px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                    >×</button>
                </div>
            )}

            {/* Detail modal */}
            {showDetailModal && selectedPayment && (
                <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Payment Detail</h2>
                            <button className="modal-close" onClick={() => setShowDetailModal(false)}>×</button>
                        </div>
                        <div className="payment-details">

                            {/* IDs */}
                            <div className="detail-row">
                                <div className="detail-item">
                                    <span className="detail-label">Payment ID:</span>
                                    <span className="detail-value" style={{ fontSize: '0.83em', wordBreak: 'break-all' }}>{selectedPayment.id}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Order ID:</span>
                                    <span className="detail-value" style={{ fontSize: '0.83em', wordBreak: 'break-all' }}>{selectedPayment.orderId}</span>
                                </div>
                            </div>

                            {/* Customer */}
                            <div className="detail-row">
                                <div className="detail-item">
                                    <span className="detail-label">Customer Name:</span>
                                    <span className="detail-value">{selectedPayment.payerName}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Phone:</span>
                                    <span className="detail-value">{selectedPayment.payerPhone}</span>
                                </div>
                            </div>

                            {/* Dates */}
                            <div className="detail-row">
                                <div className="detail-item">
                                    <span className="detail-label">Payment Date:</span>
                                    <span className="detail-value">{new Date(selectedPayment.createdAt).toLocaleString()}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Event Date:</span>
                                    <span className="detail-value">{selectedPayment.eventDate || 'N/A'}</span>
                                </div>
                            </div>

                            {/* Status */}
                            <div className="detail-row">
                                <div className="detail-item">
                                    <span className="detail-label">Payment Status:</span>
                                    <span className="detail-value">
                                        <span className="status-badge" style={{ backgroundColor: getStatusBadge(selectedPayment.status).color }}>
                                            {getStatusBadge(selectedPayment.status).text}
                                        </span>
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Order Status:</span>
                                    <span className="detail-value">{selectedPayment.orderStatus}</span>
                                </div>
                            </div>

                            {/* Services ordered */}
                            {selectedPayment.items && selectedPayment.items.length > 0 && (
                                <div className="detail-section">
                                    <h3>Services Ordered</h3>
                                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                                        {selectedPayment.items.map((item, idx) => (
                                            <li key={idx} style={{
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                padding: '8px 0', borderBottom: '1px solid #e2e8f0', gap: '12px'
                                            }}>
                                                <div>
                                                    <strong>{item.service_title || item.serviceDetails?.name || 'Unknown'}</strong>
                                                    {item.serviceDetails?.category && (
                                                        <span style={{ marginLeft: '8px', fontSize: '0.8em', color: '#888', background: '#f0f4f8', borderRadius: '4px', padding: '1px 6px' }}>
                                                            {item.serviceDetails.category}
                                                        </span>
                                                    )}
                                                    <br />
                                                    <span style={{ fontSize: '0.82em', color: '#718096' }}>
                                                        ETB {(item.price || 0).toLocaleString()}
                                                    </span>
                                                </div>
                                                <span style={{
                                                    padding: '3px 10px', borderRadius: '12px', fontSize: '0.82em', fontWeight: 600,
                                                    background: item.status === 'paid' ? '#c6f6d5' : item.status === 'rejected' ? '#fed7d7' : '#fef3c7',
                                                    color: item.status === 'paid' ? '#276749' : item.status === 'rejected' ? '#c53030' : '#92400e'
                                                }}>
                                                    {item.status || 'Pending'}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Receipt image */}
                            {selectedPayment.proofScreenshot && (
                                <div className="detail-section">
                                    <h3>Payment Receipt</h3>
                                    {isImageUrl(selectedPayment.proofScreenshot) ? (
                                        <>
                                            <img
                                                src={selectedPayment.proofScreenshot}
                                                alt="Payment receipt"
                                                style={{
                                                    width: '100%', maxHeight: '300px', objectFit: 'contain',
                                                    borderRadius: '8px', border: '1px solid #e2e8f0',
                                                    cursor: 'zoom-in', marginTop: '8px', display: 'block'
                                                }}
                                                onClick={() => setImgPreview(selectedPayment.proofScreenshot)}
                                                onError={e => { e.target.style.display = 'none'; }}
                                            />
                                            <p style={{ fontSize: '0.78em', color: '#a0aec0', marginTop: '4px' }}>
                                                Click image to enlarge · <a href={selectedPayment.proofScreenshot} target="_blank" rel="noreferrer" style={{ color: '#4299e1' }}>Open in new tab</a>
                                            </p>
                                        </>
                                    ) : (
                                        <a href={selectedPayment.proofScreenshot} target="_blank" rel="noreferrer" style={{ color: '#4299e1', textDecoration: 'underline' }}>
                                            Open Document in New Tab
                                        </a>
                                    )}
                                </div>
                            )}

                            {/* Approve / Reject buttons — only for pending */}
                            {selectedPayment.status === 'pending' && (
                                <div className="modal-footer">
                                    <button
                                        className="btn btn-danger"
                                        onClick={() => handleVerifyPayment(selectedPayment.id, false)}
                                        disabled={actionLoading}
                                    >
                                        <FiX /> Reject
                                    </button>
                                    <button
                                        className="btn btn-success"
                                        onClick={() => handleVerifyPayment(selectedPayment.id, true)}
                                        disabled={actionLoading}
                                    >
                                        <FiCheck /> Approve
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Payments;